/**
 * The workspace chrome: the sidebar, its lists and menus, the search palette, and the modals they open.
 *
 * One half of `AGENT_COPY`. The shape and the rules are documented on the
 * barrel in `../agent-copy.ts`; this file only holds entries.
 */
export const SHELL: Record<string, string> = {
	// -- the resource toolbar every list shares
	Filter: "筛选",
	Sort: "排序",
	// -- the rail
	"Workspace sidebar": "工作区侧边栏",
	"Collapse sidebar": "收起侧边栏",
	"Expand sidebar": "展开侧边栏",
	"Resize sidebar": "调整侧边栏宽度",
	"New chat": "新建对话",
	"New workflow": "新建工作流",
	Help: "帮助",
	"No chats yet": "还没有对话",
	"No workflows yet": "还没有工作流",
	"No tables yet": "还没有数据表",
	"No files yet": "还没有文件",
	"See more": "显示更多",
	"See less": "收起",
	"Loading...": "加载中…",
	"More actions": "更多操作",
	"Import workflow": "导入工作流",
	"Importing...": "导入中…",
	"Create folder": "新建文件夹",
	"Creating folder...": "创建文件夹中…",
	"Create workflow": "新建工作流",
	"Creating workflow...": "创建工作流中…",

	// -- rows and their menus
	"Chat options": "对话操作",
	"Workflow options": "工作流操作",
	"Folder options": "文件夹操作",
	"Workflow is locked": "工作流已锁定",
	"Folder is locked": "文件夹已锁定",
	Pinned: "已置顶",
	"Rename chat {name}": "重命名对话 {name}",
	"Rename workflow {name}": "重命名工作流 {name}",
	"{name} folder, expanded": "{name} 文件夹，已展开",
	"{name} folder, collapsed": "{name} 文件夹，已折叠",
	"Mark as read": "标为已读",
	"Mark as unread": "标为未读",
	Leave: "退出",
	"Upload logo": "上传标识",
	Move: "移动",
	"Move to": "移动到",
	"Close Others": "关闭其他标签页",
	"Close Tabs to the Right": "关闭右侧标签页",
	"No actions available": "没有可用操作",

	// -- deleting a workflow, folder, chat or workspace
	"Delete workflow": "删除工作流",
	"Delete workflows": "删除工作流",
	"Delete folder": "删除文件夹",
	"Delete folders": "删除文件夹",
	"Delete chat": "删除对话",
	"Delete chats": "删除对话",
	"Delete items": "删除所选项",
	"Delete workspace": "删除工作区",
	"Are you sure you want to delete this workflow? ": "确定要删除这个工作流吗？",
	"Are you sure you want to delete this folder? ": "确定要删除这个文件夹吗？",
	"Are you sure you want to delete this chat? ": "确定要删除这个对话吗？",
	"Are you sure you want to delete this workspace? ": "确定要删除这个工作区吗？",
	"Are you sure you want to delete the selected items? ": "确定要删除选中的项目吗？",
	"{n} chats": "{n} 个对话",
	"The workflow will be archived and its schedules paused. Nodes and execution history will be kept.":
		"工作流会被归档，其调度暂停；节点和运行历史会保留。",
	"All workflows and contents within these folders will be archived.": "这些文件夹里的工作流和内容都会被归档。",
	"All associated workflows and contents will be archived.": "其中的工作流和内容都会被归档。",
	"The chat and its conversation history will be archived.": "该对话及其历史记录会被归档。",
	"All selected workflows and folders, including their contents, will be archived.":
		"选中的工作流和文件夹，连同其中的内容，都会被归档。",
	"This will permanently remove all associated workflows, tables, files, logs, and knowledge bases.":
		"这会永久删除关联的全部工作流、数据表、文件、日志和知识库。",
	"You can restore it from Recently deleted in the Agent menu.": "可以在智能体菜单的「最近删除」中恢复。",
	"This action cannot be undone.": "此操作无法撤销。",
	"to confirm": "以确认",

	// -- the settings rail
	Organization: "组织",
	"Unsaved changes": "有未保存的更改",
	"Discard changes": "放弃更改",

	// -- the command palette
	"Search anything": "搜索任意内容",
	"Search anything...": "搜索任意内容…",
	"Ask Ondit": "问 Ondit",
	"Ask Ondit anything...": "向 Ondit 提问…",
	"⇥ Search": "⇥ 搜索",
	"⇥ Ask Ondit": "⇥ 问 Ondit",
	"No results found.": "没有找到结果。",
	"Start a new chat": "开始新对话",
	"New chat: {query}": "新建对话：{query}",
	Actions: "操作",
	Pages: "页面",
	Tools: "工具",
	"Tool operations": "工具操作",
	Workspaces: "工作区",
	"Connected Integrations": "已连接的集成",
	"Keyboard shortcut {keys}": "快捷键 {keys}",
	"Deploy workflow": "部署工作流",
	"Fit workflow to view": "缩放工作流以适应视图",
	"Copy workflow link": "复制工作流链接",
	"Invite teammates": "邀请成员",
	"New table": "新建数据表",
	"New column": "新建列",
	"Import CSV": "导入 CSV",
	Refresh: "刷新",
	"Edit tags": "编辑标签",
	"Switch to Dashboard": "切换到概览",
	"Switch to Logs": "切换到日志",

	// -- the support form
	Request: "请求类型",
	"Select a request type": "选择请求类型",
	"Please select a request type": "请选择请求类型",
	"Bug report": "问题报告",
	"Feature request": "功能建议",
	Other: "其他",
	Subject: "主题",
	"Subject is required": "请填写主题",
	"Brief description of your request": "一句话描述你的请求",
	Message: "内容",
	"Message is required": "请填写内容",
	"Please provide details about your request...": "请补充这次请求的细节…",
	"Attach images (optional)": "附加截图（可选）",
	"Drop images here or click to browse": "把图片拖到这里，或点击选择",
	"PNG, JPEG, WebP, GIF (max 20MB each)": "PNG、JPEG、WebP、GIF（单个不超过 20MB）",
	"Uploaded images": "已上传的图片",
	"Preview {n}": "预览 {n}",
	"Submitting...": "提交中…",
	Success: "已提交",

	// -- session and access states
	"Your session expired": "登录已过期",
	"The impersonation session expired": "模拟登录已过期",
	"{subject}. Signing you out…": "{subject}，正在退出登录…",
	"{subject}, but signing out failed.": "{subject}，但退出登录失败。",
	"Workspace access denied": "无权访问该工作区",
	"You do not have access to this workspace. Ask a workspace administrator for access or choose another workspace.":
		"你没有这个工作区的访问权限。请向工作区管理员申请，或选择其他工作区。",
	"View your workspaces": "查看你的工作区",
	"Impersonating {who}. Changes will apply to this account until you switch back.":
		"正在以 {who} 的身份操作。在切回之前，所有更改都会写入该账号。",
	"{name} ({email})": "{name}（{email}）",
	"this user": "该用户",
	"Stop impersonating": "结束模拟登录",
	"Returning...": "返回中…",
	"User avatar": "用户头像",
	"{name}’s avatar": "{name} 的头像",

	// -- the find bar
	"{index} of {count}": "第 {index} / {count} 条",
	"{index} of {count}+": "第 {index} / {count}+ 条",
	"Show replace": "显示替换",
	"Hide replace": "隐藏替换",
	"Clear search": "清除搜索",
	"Previous match (Shift+Enter)": "上一处匹配（Shift+Enter）",
	"Next match (Enter)": "下一处匹配（Enter）",
	"Close find": "关闭查找",
	"Close (Esc)": "关闭（Esc）",
	Replace: "替换",
	"Replace in document": "在文档中替换",
	"Replace all matches": "替换全部匹配",
	"Narrow the search to replace all": "缩小搜索范围后才能全部替换",

	// -- resource lists and their empty states
	"Search...": "搜索…",
	"Select row": "选择该行",
	Path: "路径",
	Docs: "文档",
	"No results for “{query}”": "没有与“{query}”匹配的结果",
	"Searched every folder in this workspace.": "已搜索该工作区的所有文件夹。",
	"Filters apply to this folder only.": "筛选只作用于当前文件夹。",
	"Clear search and filters": "清除搜索和筛选",
	"Clear filters": "清除筛选",
	"Upload files to share them across your team and every agent.": "上传文件，供团队和所有智能体共用。",
	"Upload documents so your agents can search this base.": "上传文档，智能体就能检索这个知识库。",
	"Upload documents to give your agents a memory they can search.": "上传文档，为智能体建立可检索的记忆。",
	"Create a table to store structured data your agents can read and write.":
		"新建数据表，存放智能体可读写的结构化数据。",
	"Every workflow execution lands here, traced block by block.": "每次工作流运行都会记录在这里，逐个节点可追溯。",
	"{count} selected · select {max} or fewer": "已选 {count} 项 · 最多可选 {max} 项",
	"{count} selected items": "已选 {count} 项",
	"selected item": "选中的项目",
	"{action} selected items": "{action}选中项",
	"{action} {count} item": "{action} {count} 项",
	"{action} {count} items": "{action} {count} 项",

	// -- relative times in list cells
	Now: "刚刚",
	"{elapsed} ago": "{elapsed}前",
	"{count} minute": "{count} 分钟",
	"{count} minutes": "{count} 分钟",
	"{count} hour": "{count} 小时",
	"{count} hours": "{count} 小时",
	"{count} day": "{count} 天",
	"{count} days": "{count} 天",
	"{month} {day}, {year}": "{year}年{month}{day}日",
	January: "1月",
	February: "2月",
	March: "3月",
	April: "4月",
	May: "5月",
	June: "6月",
	July: "7月",
	August: "8月",
	September: "9月",
	October: "10月",
	November: "11月",
	December: "12月",

	// -- inviting people and their roles
	"Invite teammates to {target}": "邀请成员加入{target}",
	workspace: "工作区",
	organization: "组织",
	"This workspace": "当前工作区",
	Emails: "邮箱地址",
	"Enter emails": "输入邮箱地址",
	"Only administrators can invite new teammates": "只有管理员可以邀请新成员",
	"Invalid email": "邮箱地址无效",
	"You cannot invite yourself": "不能邀请自己",
	"Select workspaces": "选择工作区",
	"Search workspaces...": "搜索工作区…",
	"Workspace access": "工作区权限",
	"Select access": "选择权限",
	"Organization membership": "组织成员身份",
	"Select membership": "选择成员身份",
	Role: "角色",
	"Select role": "选择角色",
	Admin: "管理员",
	Member: "成员",
	External: "外部协作者",
	Read: "只读",
	Write: "可编辑",
	"View only": "仅查看",
	"Edit content": "编辑内容",
	"Full access": "完全访问",
	"Organization member": "组织成员",
	"Organization admin": "组织管理员",
	"Joins your organization and can manage it. Adds a seat.": "加入并可管理你的组织，占用一个席位。",
	"Access to the selected workspaces only — no seat. Only available for people already on a paid Ondit plan.":
		"只能访问所选工作区，不占席位；仅适用于已有 Ondit 付费套餐的人。",
	"Only {count} seats available — invites beyond that may fail. External collaborators and existing members do not use seats.":
		"只剩 {count} 个席位，超出的邀请可能失败。外部协作者和已有成员不占席位。",
	"Send invites": "发送邀请",
	"Sending...": "发送中…",
	"{count} members added": "已添加 {count} 位成员",
	"{count} invites sent": "已发出 {count} 份邀请",
	"None of the {count} invitations could be sent.": "{count} 份邀请全部发送失败。",
	"{count} of {total} invitations could not be sent.": "{total} 份邀请中有 {count} 份发送失败。",
	"And {count} more.": "另有 {count} 条。",
	"An unexpected error occurred. Please try again.": "出现了预期之外的错误，请重试。",
	"Add people": "添加成员",
	"Couldn’t add people. {reason}": "添加成员失败。{reason}",
	"Couldn’t add {count} of {total} people. {reason}": "{total} 人中有 {count} 人添加失败。{reason}",
	"Workspace owner": "工作区所有者",
	"Workspace billing account": "工作区付费账号",
	"Organization admins are automatically workspace admins": "组织管理员自动拥有工作区管理员权限",
	"Organization admins are automatically workspace admins. Change their organization role to remove them.":
		"组织管理员自动拥有工作区管理员权限。要移除，请先更改其组织角色。",
	"Reassign billing before removing the workspace billing account": "移除工作区付费账号前，请先转移付费主体",
	"Workspace admins are automatically credential admins": "工作区管理员自动拥有凭据管理员权限",
	"Workspace admins are automatically skill editors": "工作区管理员自动拥有技能编辑权限",

	// -- connecting an integration
	"Connect {provider}": "连接 {provider}",
	"The “{tool}” tool requires access to your account.": "“{tool}”工具需要访问你的账号。",
	Environment: "环境",
	"Environment URL": "环境地址",
	"Copy environment URL": "复制环境地址",
	"This connection is locked to the environment selected by the workflow or credential.":
		"该连接已锁定为工作流或凭据选定的环境。",
	"This credential is restricted to one public-cloud Dataverse environment.": "该凭据仅限一个公有云 Dataverse 环境。",
	"Display name is required.": "请填写显示名称。",
	"An integration named “{name}” already exists.": "已存在名为“{name}”的集成。",
	"Integration name": "集成名称",
	"Redirect URI": "回调地址",
	"Copy redirect URI": "复制回调地址",
	"Permissions requested": "申请的权限",
	New: "新增",
	"Complete every OAuth app configuration field.": "请填写全部 OAuth 应用配置项。",
	"Connecting...": "连接中…",

	// -- importing from another browser
	"Import from your browser": "从浏览器导入",
	"Copy cookies, saved passwords, and address-bar suggestions into Ondit. Your other browser stays unchanged and nothing is uploaded. Future changes are not synced.":
		"把 Cookie、已保存的密码和地址栏建议复制到 Ondit。原浏览器不会改动，也不会上传任何内容；此后的变更不会同步。",
	"Passwords stay encrypted on this device until you delete them or sign out of Ondit. Website sessions may still expire.":
		"密码在本机加密保存，直到你删除它们或退出 Ondit。网站的登录状态仍可能过期。",
	"Looking for browser profiles…": "正在查找浏览器配置…",
	"No supported browser profiles were found on this device.": "本机没有找到受支持的浏览器配置。",
	"Select a browser": "选择浏览器",
	"Select a profile": "选择配置",
	"Browser import is not available on this device.": "本机不支持从浏览器导入。",
	"Could not load browser profiles. Close this dialog and try again.": "无法读取浏览器配置，请关闭此对话框后重试。",
	"Importing from another browser is only supported on macOS.": "从其他浏览器导入目前仅支持 macOS。",
	"Could not find that browser profile.": "找不到该浏览器配置。",
	"Ondit needs your permission to read that browser’s saved data. Allow the Keychain prompt and try again.":
		"Ondit 需要你的授权才能读取该浏览器的数据。请在钥匙串提示中允许后重试。",
	"Could not read that browser’s data. Try quitting the other browser, then import again.":
		"无法读取该浏览器的数据。请先退出该浏览器，再重新导入。",
	"That browser stores its data in a format Ondit cannot read yet.": "该浏览器的数据格式 Ondit 暂时无法读取。",
	"Nothing from that profile could be imported.": "该配置中没有可导入的内容。",
	"This device cannot store passwords securely, so saved passwords were not imported.":
		"本机无法安全保存密码，因此没有导入已保存的密码。",
	"Could not import from that browser.": "无法从该浏览器导入。",
	"Could not import from that browser": "无法从该浏览器导入",
	"Cookies: {reason}": "Cookie：{reason}",
	"Passwords: {reason}": "密码：{reason}",
	"{count} cookie": "{count} 条 Cookie",
	"{count} cookies": "{count} 条 Cookie",
	"{count} password": "{count} 个密码",
	"{count} passwords": "{count} 个密码",
	"{first} and {second}": "{first}和{second}",
	"Imported {what}": "已导入{what}",
	"{summary} from {browser}": "{summary}（来自 {browser}）",
	"{summary} from {browser}.": "{summary}（来自 {browser}）。",
	"No new data was imported. Existing saved passwords were kept.": "没有导入新数据，已保存的密码保持不变。",
	"Import finished, but the browser could not refresh. Try reopening it.":
		"导入完成，但浏览器未能刷新，请尝试重新打开。",

	// -- a message's actions
	"Copy message": "复制消息",
	"Copied message": "已复制消息",
	Like: "赞",
	Dislike: "踩",
	"Good response": "回答不错",
	"Bad response": "回答不佳",
	"Fork in new chat": "在新对话中分支",
	"{count} files could not be copied to the fork": "有 {count} 个文件未能复制到分支",
	"Failed to fork chat": "分支对话失败",
	"Give feedback": "反馈",
	Feedback: "反馈",
	"Tell us what was helpful...": "说说哪里有帮助…",
	"Tell us what went wrong...": "说说哪里出了问题…",

	// -- the custom tool editor
	"Available parameters:": "可用参数：",
	"Available Parameters": "可用参数",
	"Start typing a parameter name for autocomplete.": "输入参数名即可自动补全。",
	"Describe what to generate...": "描述要生成的内容…",
	Generate: "生成",

	// -- shared chrome: loaders, charts, passwords, PII patterns
	"Thought for a moment": "已思考片刻",
	"Using tools…": "正在调用工具…",
	Ready: "就绪",
	"Orchestrating…": "编排中…",
	"Working…": "执行中…",
	"In formation…": "编队中…",
	"Standing by…": "待命中…",
	"No data": "暂无数据",
	"Image viewer": "图片查看器",
	"Generate password": "生成密码",
	"Copy password": "复制密码",
	"Show password": "显示密码",
	"Hide password": "隐藏密码",
	Show: "显示",
	Hide: "隐藏",
	"Pattern (regex)": "匹配模式（正则）",
	"Remove pattern": "删除规则",
	"Add pattern": "添加规则",
};
